import React, { useEffect, useState } from 'react';
import {
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Icon,
  Button,
  Tooltip,
  TablePagination,
  TableContainer,
} from '@mui/material';
import clsx from 'clsx';
import Api from '../../../services/api';
import { ArrowDownward, ArrowUpward } from '@mui/icons-material';
import DataTableHeader from './DataTableHeader';

import { checkHasAccess, checkHasAccessToAction, generateMongoQuery } from 'app/utils/utils';
import ConfirmationDialog from 'app/components/ConfirmationDialog/ConfirmationDialog';
import * as momentj from 'moment-jalaali';
import ImportModal from 'app/components/ImportModal/ImportModal';
import { useNavigate, useLocation } from 'react-router-dom';
import { translate } from 'translate';
import useAuth from 'app/hooks/useAuth';
const DataTable = ({
  columns,
  apipath,
  title,
  filtersOn,
  handleAdd,
  defaultFilter,
  defaultSort,
  hideActions = [],
  actions = [],
  extraActions = [],
  ...props
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  let initialValues = {};
  for (const f of filtersOn) {
    initialValues[f.key] = '';
  }

  const [rowsPerPage, setRowsPerPage] = useState(5);
  // dynamic columns
  const [dynamicColumns, setDynamicColumns] = useState(columns);
  const [page, setPage] = useState(0);
  const [data, setData] = useState([]);
  const [sort, setSort] = useState({ key: defaultSort ? defaultSort : 'created_at', dir: 'desc' });
  const [count, setCount] = useState();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState(initialValues);
  const [deleteItem, setDeleteItem] = useState(null);
  const [activateItem, setActivateItem] = useState(null);
  const [refresh, setRefresh] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const { user } = useAuth();
  const roles = user.roles;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };
  const handleSortChange = (c) => {
    if (c.key === sort.key) {
      setSort({ key: c.key, dir: sort.dir === 'desc' ? 'asc' : 'desc' });
    } else {
      setSort({ key: c.key, dir: 'desc' });
    }
  };
  const onFilter = (values) => {
    setFilters(values);
  };

  useEffect(() => {
    // console.log('here');
    Api.post(`${apipath}/list`, {
      limit: rowsPerPage,
      offset: page * rowsPerPage,
      $orderby: { [sort.key]: sort.dir === 'desc' ? -1 : 1 },
      ...generateMongoQuery({ ...filters, ...defaultFilter }),
    })
      .then((d) => {
        setData(d.data);
        // cross check columns with data keys and set dynamic columns
        if (d.data[0] && d.data[0].uuid) {
          const dynamicCols = [];
          for (const c of columns) {
            if (typeof d.data[0][c.key] !== 'undefined') {
              // console.log(c.auth);
              if (c.auth) {
                if (checkHasAccess(roles, c.auth)) {
                  dynamicCols.push(c);
                }
              } else {
                dynamicCols.push(c);
              }
            }
          }
          // console.log(dynamicCols);
          setDynamicColumns(dynamicCols);
        }
        // if (d.data[0])
      })
      .catch((e) => {
        console.log(e);
      });
  }, [page, rowsPerPage, sort, filters, refresh]);

  useEffect(() => {
    Api.post(`${apipath}/list`, {
      get_count: true,
      ...generateMongoQuery({ ...filters, ...defaultFilter }),
    }).then((d) => {
      setCount(d.data);
    });
  }, [filters]);

  const handleDelete = async (item) => {
    setDeleteItem(null);
    await Api.delete(`${apipath}/${deleteItem.uuid}`);
    setRefresh(!refresh);
  };
  const handleActivate = async (item) => {
    setActivateItem(null);
    await Api.undelete(`${apipath}/${activateItem.uuid}`);
    setRefresh(!refresh);
  };

  const handleExport = (values) => {
    Api.downloadWithFilters(
      `${apipath}/export/csv`,
      generateMongoQuery({ ...values, ...defaultFilter }),
    );
  };
  const handleImport = () => {
    setShowImport(true);
  };

  const renderItem = (col, entity, index) => {
    if (col && col.key && col.key.indexOf('.') > -1 && entity) {
      const firstKey = col.key.split('.')[0];

      if (entity[firstKey]) {
        console.log('here', {
          col: { ...col, key: col.key.split('.').slice(1).join('.') },
          entity: entity[firstKey],
          index,
        });
        return renderItem(
          { ...col, key: col.key.split('.').slice(1).join('.') },
          entity[firstKey],
          index,
        );
      }
    }
    return col.render
      ? col.render(entity, index)
      : Array.isArray(entity[col.key])
      ? entity[col.key].map((a) => translate(a)).join(', ')
      : col.type === 'date'
      ? momentj(entity[col.key]).format('jYYYY-jMM-jDD HH:mm')
      : col.type === 'price'
      ? entity[col.key]?.toLocaleString()
      : translate(entity[col.key]);
  };

  return (
    <div className="w-full overflow-auto min-h-[400px]">
      <DataTableHeader
        {...props}
        filters={filters}
        title={translate(title)}
        filtersOn={filtersOn}
        onSubmit={onFilter}
        handleAdd={handleAdd}
        showCreate={checkHasAccessToAction('create', hideActions, actions, roles)}
        handleExport={handleExport}
        handleImport={handleImport}
      />
      <TableContainer>
        <Table
          sx={{ minWidth: dynamicColumns.length > 10 ? 1500 : 700 }}
          className="whitespace-pre"
        >
          <TableHead>
            <TableRow>
              <TableCell className={clsx('px-0', 'text-right')} width={75}>
                {translate('row')}
              </TableCell>
              {dynamicColumns.map((c, index) => (
                <TableCell
                  width={c.width ? c.width : c.type === 'date' ? 150 : 120}
                  key={`${c}${index}`}
                  className={clsx('px-0', 'text-right')}
                >
                  {c.sort ? (
                    <Tooltip title="Sort">
                      <Button
                        endIcon={
                          sort.key === c.key ? (
                            sort.dir === 'desc' ? (
                              <ArrowDownward />
                            ) : (
                              <ArrowUpward />
                            )
                          ) : null
                        }
                        onClick={() => handleSortChange(c)}
                      >
                        <span className="ml-2">{translate(c.name)}</span>
                      </Button>
                    </Tooltip>
                  ) : (
                    translate(c.name)
                  )}
                </TableCell>
              ))}
              {hideActions.length < 4 && (
                <TableCell width={150} className="px-0">
                  {translate('Actions')}
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((d, index) => (
              <TableRow hover key={index}>
                <TableCell className="">{index + page * rowsPerPage + 1}</TableCell>
                {dynamicColumns.map((c, index) => (
                  <TableCell
                    dir="ltr"
                    key={`c${index}`}
                    className="px-0 ltr capitalize"
                    align="right"
                  >
                    {renderItem(c, d, index)}
                  </TableCell>
                ))}
                <TableCell className="px-0">
                  {/* <IconButton>
                  <Icon color="error">close</Icon>
                </IconButton> */}
                  {checkHasAccessToAction('edit', hideActions, actions, roles) && (
                    <IconButton
                      onClick={() => {
                        navigate(`${location.pathname}/edit/${d.uuid}`);
                      }}
                    >
                      <Icon>edit</Icon>
                    </IconButton>
                  )}
                  {checkHasAccessToAction('view', hideActions, actions, roles) && (
                    <IconButton
                      onClick={() => {
                        navigate(`${location.pathname}/view/${d.uuid}`);
                      }}
                    >
                      <Icon>arrow_back</Icon>
                    </IconButton>
                  )}
                  {checkHasAccessToAction('delete', hideActions, actions, roles) && (
                    <>
                      {d.deleted_at ? (
                        <Button
                          onClick={() => {
                            setActivateItem(d);
                          }}
                          variant="outlined"
                        >
                          {translate('activate')}
                        </Button>
                      ) : (
                        <IconButton
                          className="text-red-600"
                          onClick={() => {
                            setDeleteItem(d);
                          }}
                        >
                          <Icon>delete</Icon>
                        </IconButton>
                      )}
                    </>
                  )}
                  {extraActions.map((a, index) => (
                    <>
                      {checkHasAccess(roles, a.auth) ? (
                        <Button
                          key={index}
                          onClick={() => {
                            navigate(`${location.pathname}/${a.path}/${d.uuid}`);
                          }}
                        >
                          {translate(a.label)}
                        </Button>
                      ) : null}
                    </>
                  ))}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        className="px-4"
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={count || 100}
        rowsPerPage={rowsPerPage}
        page={page}
        backIconButtonProps={{
          'aria-label': 'Previous Page',
        }}
        nextIconButtonProps={{
          'aria-label': 'Next Page',
        }}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      {deleteItem && (
        <ConfirmationDialog
          title={`${translate('deactivate')} ${deleteItem[dynamicColumns[0]?.key]}`}
          open={Boolean(deleteItem)}
          onConfirmDialogClose={() => setDeleteItem(null)}
          text={`${translate('Are you sure you want to delete')}`}
          onYesClick={handleDelete}
        />
      )}
      {activateItem && (
        <ConfirmationDialog
          title={`${translate('activate')} ${activateItem[dynamicColumns[0]?.key]}`}
          open={Boolean(activateItem)}
          onConfirmDialogClose={() => setActivateItem(null)}
          text={`${translate('Are you sure you want to activate')}`}
          onYesClick={handleActivate}
        />
      )}
      {showImport && <ImportModal open={showImport} onModalClose={() => setShowImport(false)} />}
    </div>
  );
};

export default DataTable;
